import type { As400NumberFormat } from '@erp/rpc';
import { upcProblem, type UpcProblem } from './as400Upc';
import type { RowMode } from './as400RowKeys';

/**
 * The kinds of rule a review-grid cell can be checked against (GEN-2748), and
 * the vocabulary every rule speaks: what a rule reads ({@link CellRow}), what
 * it reports ({@link CellViolationDetail}), and how hard ({@link
 * CellSeverity}). Pure. The engine that runs rules lives in as400CellRules,
 * and each worksheet's own rules in sheetRules/.
 */

/**
 * How hard a broken rule stops the author. An error is a value the AS/400
 * can't take, and Save refuses it. A warning is a value worth a second look:
 * it is outlined and listed the same way, but Save goes through.
 */
export type CellSeverity = 'error' | 'warning';

/** What is wrong with a value a number format refused. */
export type NumberFormatProblem =
  /** Not digits with at most one point. */
  | 'not-a-number'
  /** A point in a column that takes whole numbers. */
  | 'whole-number'
  /** More digits before the point than the mask has. */
  | 'integer-digits'
  /** More digits after the point than the mask has. */
  | 'fraction-digits';

/**
 * What a rule found wrong, discriminated by the rule's stable id and carrying
 * what the message needs to say how to fix it. Each new rule kind adds a
 * member here, and its wording in as400CellViolationMessages.
 */
export type CellViolationDetail =
  | { rule: 'max-length'; limit: number; length: number }
  | { rule: 'exact-length'; length: number; actual: number }
  | {
      rule: 'number-format';
      problem: NumberFormatProblem;
      format: As400NumberFormat;
    }
  | {
      rule: 'allowed-values';
      allowed: readonly string[];
      blankAllowed: boolean;
    }
  | { rule: 'not-on-allowlist' }
  | { rule: 'not-in-use'; confirmWith: string }
  | {
      rule: 'required-flag';
      /** The field whose value calls for the flag, and that value. */
      cause: { field: string; value: string };
      /** What the flag must hold. */
      required: string;
    }
  | { rule: 'no-effect'; instead: readonly string[] }
  | {
      rule: 'reserved-positions';
      /** Positions 1..limit are the author's; the rest belong to `owner`. */
      limit: number;
      length: number;
      owner: string;
    }
  | { rule: 'item-prefix'; itemField: string; prefix: string }
  /** A supplier section with data and no Y Ship Direct flag (GEN-2764). */
  | { rule: 'ship-direct'; section: number }
  /** A new item's supplier section with no minimum quantity (GEN-2770). */
  | { rule: 'min-quantity-required'; section: number }
  | { rule: 'pricing-factor'; expected: number; sellUom: string }
  | { rule: 'purchasing-factor-required'; section: number }
  | { rule: 'purchasing-factor-must-be-one'; stockUom: string }
  /** A change (or clear) of `field` without this, its Y flag. */
  | { rule: 'change-flag'; field: string; cleared: boolean }
  /** `field` changed and this cell, which goes with it, did not. */
  | { rule: 'paired-edit'; field: string }
  /** A value that can't load without `field` filled in. */
  | { rule: 'needs-field'; field: string }
  /** A field that should stay 0 (GEN-2770's minimum amount). */
  | { rule: 'should-be-zero' }
  /** A dimension of 0, which fails the upload (GEN-2775). */
  | { rule: 'zero-dimension' }
  | { rule: 'item-add-required'; missingLanguages: readonly string[] }
  | { rule: 'upc'; problem: UpcProblem }
  | { rule: 'max-amount'; limit: number; siblingValue?: string }
  | { rule: 'not-loaded' }
  | {
      rule: 'warning-code-placement';
      slot: string;
      required: boolean;
    }
  | { rule: 'slash-count'; expected: number; actual: number }
  | { rule: 'whitespace-only' }
  | { rule: 'cannot-clear' }
  | { rule: 'required' }
  | {
      rule: 'move-to-description';
      limit: number;
      length: number;
      /**
       * The blank-out marker that clears the cell on a change row, where a
       * blank means "don't change"; null on an add, where blank is blank.
       */
      clearWith: string | null;
      /** What goes in the English description (its first line). */
      description: string;
      /** What continues on the Item Add lines, from line 10 down. */
      additionalLines: readonly AdditionalDescriptionLine[];
      /** The Item Add lines' character limit, which the split follows. */
      lineLimit: number;
      /** The language (IDLANG) the Item Add lines go under: English. */
      language: string;
      /** Characters left over once every Item Add line is full. */
      leftOver: number;
    }
  | {
      rule: 'item-number-format';
      problem: 'disallowed-character' | 'truncation-marker';
    }
  | { rule: 'item-number-collision'; suggestion: string | null };

/** One Item Add (additional description) line a long value continues on. */
export interface AdditionalDescriptionLine {
  line: number;
  text: string;
}

/** One broken rule on one cell. */
export interface CellViolation {
  /** The field (edit key) of the cell the violation marks. */
  field: string;
  /** The cell's value as checked. */
  value: string | null;
  severity: CellSeverity;
  detail: CellViolationDetail;
}

/** A row's cells as a rule reads them: effective values plus edit state. */
export interface CellRow {
  /** The field's effective value (unsaved edit, else persisted). */
  value: (field: string) => string | null;
  /** Whether the field carries an unsaved edit. */
  edited: (field: string) => boolean;
  /**
   * What the AS/400 itself holds for the field — the value a "Reset to AS/400
   * value" writes, before any saved or unsaved edit. Absent where the row
   * can't tell; a fill then never reads a value as reset.
   */
  as400Value?: (field: string) => string | null;
  /** Whether the row adds a record or changes one (see rowModeOf). */
  mode: RowMode;
  itemAdds?: (
    itemNumber: string,
    cdb?: string | null,
  ) => readonly { itemNumber: string; language: string | null }[];
  stockUoms?: (itemNumber: string, cdb?: string | null) => string | null;
  siblings?: readonly CellRow[];
}

/**
 * One rule on one field. `dependsOn` names the other fields the rule reads
 * (a paired or cross-field rule), so editing any of them re-checks this cell.
 * `severity` defaults to error; `modes`, when set, limits the rule to add or
 * to change rows.
 */
export interface CellRule {
  field: string;
  dependsOn?: readonly string[];
  severity?: CellSeverity;
  modes?: readonly RowMode[];
  evenWhenUnedited?: boolean;
  check: (value: string | null, row: CellRow) => CellViolationDetail | null;
  /**
   * The rule enforces the column's own character limit itself (with its own
   * advice), so the generic limit check is not run on this field as well.
   */
  replacesColumnLimit?: boolean;
}

/**
 * Fill other fields when any of `when` changes. `fill` reads the row as the
 * batch leaves it and names the fields to set; an empty record (or null) sets
 * nothing. `modes`, when set, limits the fill to add or to change rows. Run by
 * as400CellAutoFill in the grid's beforeChange, so the fill lands in the same
 * batch as the edit that called for it.
 */
export interface CellAutoFill {
  when: readonly string[];
  modes?: readonly RowMode[];
  /**
   * Also run when a reset ("Reset to AS/400 value", "Reset to last saved
   * value") writes `when` back. Off by default: a reset puts values back as
   * they were, so most fills have nothing to add to it.
   */
  onReset?: boolean;
  fill: (row: CellRow) => Readonly<Record<string, string | null>> | null;
}

/** A value a new row starts with; null leaves the field blank. */
export interface AddRowDefault {
  field: string;
  value: (row: CellRow) => string | null;
}

/**
 * One worksheet's own rules, auto-fills and add-row defaults (sheetRules/),
 * on top of what its columns declare in the contract (character limits,
 * number formats).
 */
export interface WorksheetRules {
  rules: readonly CellRule[];
  autoFills: readonly CellAutoFill[];
  addDefaults: readonly AddRowDefault[];
}

/** The rule, reported as a warning: shown and listed, never blocking Save. */
export function asWarning(rule: CellRule): CellRule {
  return { ...rule, severity: 'warning' };
}

/** The rule, checked only on rows of the given mode(s). */
export function onlyIn(
  modes: RowMode | readonly RowMode[],
  rule: CellRule,
): CellRule {
  return { ...rule, modes: typeof modes === 'string' ? [modes] : modes };
}

export function upcRule(
  itemField: string,
  manufacturerField: string,
): CellRule {
  return {
    field: itemField,
    dependsOn: [manufacturerField],
    check(value, row) {
      const problem = upcProblem(row.value(manufacturerField), value);
      return problem ? { rule: 'upc', problem } : null;
    },
  };
}

/** The value may be at most `limit` characters (a cleared cell always fits). */
export function maxLengthRule(field: string, limit: number): CellRule {
  return {
    field,
    check(value) {
      if (value === null || value.length <= limit) return null;
      return { rule: 'max-length', limit, length: value.length };
    },
  };
}

/**
 * The value must be exactly `length` characters — a fixed-width code, where a
 * short one is as wrong as a long one. A cleared cell passes: whether a blank
 * is allowed is a separate rule's call.
 */
export function exactLengthRule(field: string, length: number): CellRule {
  return {
    field,
    check(value) {
      if (value === null || value.length === length) return null;
      return { rule: 'exact-length', length, actual: value.length };
    },
  };
}

// Digits with at most one point; either side of it may be empty.
const DECIMAL_NUMBER = /^(\d*)(?:\.(\d*))?$/;

/**
 * What is wrong with `value` under `format`, or null when it fits. Shorter
 * than the mask is always fine; longer on either side of the point is not.
 */
export function numberFormatProblem(
  value: string,
  format: As400NumberFormat,
): NumberFormatProblem | null {
  if (format.clearValues?.includes(value)) return null;
  const match = DECIMAL_NUMBER.exec(value);
  const whole = match?.[1] ?? '';
  const fraction = match?.[2];
  if (!match || (whole === '' && !fraction)) return 'not-a-number';
  if (fraction !== undefined && format.fractionDigits === 0) {
    return 'whole-number';
  }
  if (
    format.integerDigits !== undefined &&
    whole.length > format.integerDigits
  ) {
    return 'integer-digits';
  }
  if ((fraction?.length ?? 0) > format.fractionDigits) {
    return 'fraction-digits';
  }
  return null;
}

/** The value must follow the column's number mask (a cleared cell passes). */
export function numberFormatRule(
  field: string,
  format: As400NumberFormat,
): CellRule {
  return {
    field,
    check(value) {
      if (value === null) return null;
      const problem = numberFormatProblem(value, format);
      return problem ? { rule: 'number-format', problem, format } : null;
    },
  };
}

/**
 * The value must be one of `allowed`, exactly as written (case-sensitive), or
 * blank when `blankAllowed`.
 */
export function allowedValuesRule(
  field: string,
  allowed: readonly string[],
  {
    blankAllowed = false,
    listAllowed = true,
  }: { blankAllowed?: boolean; listAllowed?: boolean } = {},
): CellRule {
  return {
    field,
    check(value) {
      if (value === null ? blankAllowed : allowed.includes(value)) return null;
      return listAllowed
        ? { rule: 'allowed-values', allowed, blankAllowed }
        : { rule: 'not-on-allowlist' };
    },
  };
}

/**
 * The field isn't loaded today: any value in it should be confirmed with
 * `confirmWith` first. Usually declared as a warning ({@link asWarning}).
 */
export function notInUseRule(field: string, confirmWith: string): CellRule {
  return {
    field,
    check(value) {
      return value === null ? null : { rule: 'not-in-use', confirmWith };
    },
  };
}

/**
 * When `cause.field` holds `cause.value`, this flag must hold `required` —
 * otherwise the AS/400 ignores the change the cause asks for. The backstop
 * for an auto-fill ({@link CellAutoFill}) a value was typed back over. On a
 * change row the cause counts only when it is edited: a value the record
 * already holds asks for nothing, so the flag is free to stay blank.
 */
export function requiredFlagRule(
  field: string,
  cause: { field: string; value: string },
  required: string,
): CellRule {
  return {
    field,
    dependsOn: [cause.field],
    check(value, row) {
      if (value === required || row.value(cause.field) !== cause.value) {
        return null;
      }
      if (row.mode === 'change' && !row.edited(cause.field)) return null;
      return { rule: 'required-flag', cause, required };
    },
  };
}

/**
 * `flag` must be Y once `target` is edited — or, with `clearsOnly`, once it is
 * cleared: the AS/400 only takes the new value (or the blank) with the flag.
 * A new row has nothing to clear, so a blank there needs no flag (every cell
 * of a new row counts as edited).
 */
export function changeFlagRule(
  flag: string,
  target: string,
  { clearsOnly = false }: { clearsOnly?: boolean } = {},
): CellRule {
  return {
    field: flag,
    dependsOn: [target],
    check(value, row) {
      if (value === 'Y' || !row.edited(target)) return null;
      const cleared = row.value(target) === null;
      if (cleared && row.mode === 'add') return null;
      if (clearsOnly && !cleared) return null;
      return { rule: 'change-flag', field: target, cleared };
    },
  };
}

/**
 * This field should change whenever `cause` does: the two say one thing (a
 * warning code and the remark that explains it), so changing one alone is
 * worth a second look. Usually declared as a warning ({@link asWarning}). A
 * change row asks for an edit here once `cause` is edited; a new row, where
 * every cell counts as edited, asks only for a value here once `cause` has
 * one.
 */
export function pairedEditRule(field: string, cause: string): CellRule {
  return {
    field,
    dependsOn: [cause],
    check(value, row) {
      if (!row.edited(cause)) return null;
      const missing =
        row.mode === 'add'
          ? row.value(cause) !== null && value === null
          : !row.edited(field);
      return missing ? { rule: 'paired-edit', field: cause } : null;
    },
  };
}

/**
 * Set `flag` to Y whenever `target` is edited (a clear included) — but not
 * for a blank on a new row, which clears nothing (see changeFlagRule). When a
 * change row's `target` goes back to what the AS/400 holds (a reset, or the
 * old value typed back), the flag goes back to the AS/400's too: there is no
 * change left for it to carry. A clear is not a reset: blanking a value the
 * AS/400 has is a change, and keeps the Y.
 */
export function changeFlagFill(flag: string, target: string): CellAutoFill {
  return {
    when: [target],
    onReset: true,
    fill(row) {
      const value = row.value(target);
      if (row.mode === 'add') return value === null ? null : { [flag]: 'Y' };
      if (row.as400Value && value === row.as400Value(target)) {
        return { [flag]: row.as400Value(flag) };
      }
      return { [flag]: 'Y' };
    },
  };
}

/**
 * `value` is accepted but does nothing on the AS/400; `instead` are the
 * values that do. Usually declared as a warning ({@link asWarning}).
 */
export function noEffectRule(
  field: string,
  value: string,
  instead: readonly string[],
): CellRule {
  return {
    field,
    check(actual) {
      return actual === value ? { rule: 'no-effect', instead } : null;
    },
  };
}

/** A run of slashes only: the AS/400's blank-out marker, read as blank. */
export function isSlashRun(value: string): boolean {
  return /^\/+$/.test(value);
}

export function skipsWhitespaceOnlyWarn(field: string): boolean {
  const key = field.toUpperCase();
  return key === 'ICODE3' || key.startsWith('ICAT') || key.startsWith('IAT');
}

export function slashCountRule(field: string, expected: number): CellRule {
  return {
    field,
    check(value) {
      if (value === null || !isSlashRun(value) || value.length === expected) {
        return null;
      }
      return { rule: 'slash-count', expected, actual: value.length };
    },
  };
}

const SLASH_COUNT_AUTOCORRECT_MAX = 2;
const LONG_MARKER_MIN_LENGTH = 4;
const LONG_MARKER_SNAP_MIN = 3;

export function correctedSlashCount(
  value: string,
  marker: string,
): string | null {
  if (!marker || !isSlashRun(value) || value.length === marker.length) {
    return null;
  }
  if (marker.length >= LONG_MARKER_MIN_LENGTH) {
    return value.length >= LONG_MARKER_SNAP_MIN ? marker : null;
  }
  return Math.abs(value.length - marker.length) <= SLASH_COUNT_AUTOCORRECT_MAX
    ? marker
    : null;
}

export function whitespaceOnlyRule(field: string): CellRule {
  return {
    field,
    check(value) {
      if (value === null || value.length === 0 || !/^[ \t]+$/.test(value)) {
        return null;
      }
      return { rule: 'whitespace-only' };
    },
  };
}

/**
 * Only the first `limit` positions of the field are the author's: the AS/400
 * writes the rest from `owner`, and ignores what is typed there. Trailing
 * spaces past the limit carry nothing and pass, as does a slash run (the
 * blank-out marker spans the whole field).
 */
export function reservedPositionsRule(
  field: string,
  limit: number,
  owner: string,
): CellRule {
  return {
    field,
    check(value) {
      if (value === null || isSlashRun(value)) return null;
      if (value.slice(limit).trim() === '') return null;
      return {
        rule: 'reserved-positions',
        limit,
        length: value.length,
        owner,
      };
    },
  };
}

/**
 * `value` in this field is expected only on items whose number (in
 * `itemField`) starts with `prefix`. Usually declared as a warning.
 */
export function itemPrefixRule(
  field: string,
  value: string,
  { itemField, prefix }: { itemField: string; prefix: string },
): CellRule {
  return {
    field,
    dependsOn: [itemField],
    check(actual, row) {
      if (actual !== value) return null;
      const item = (row.value(itemField) ?? '').trim().toUpperCase();
      return item.startsWith(prefix)
        ? null
        : { rule: 'item-prefix', itemField, prefix };
    },
  };
}

export function parseDecimalAmount(value: string | null): number | null {
  if (value === null || !DECIMAL_NUMBER.test(value)) return null;
  if (value === '.' || value === '') return null;
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : null;
}

export function amountExceedsLimit(
  value: string | null,
  limit: number,
): boolean {
  const amount = parseDecimalAmount(value);
  return amount !== null && amount > limit;
}

function offendingSiblingAmount(
  row: CellRow,
  field: string,
  limit: number,
): string | null {
  for (const sibling of row.siblings ?? []) {
    if (!sibling.edited(field)) continue;
    const siblingValue = sibling.value(field);
    if (amountExceedsLimit(siblingValue, limit)) {
      return siblingValue;
    }
  }
  return null;
}

export function maxAmountRule(field: string, limit: number): CellRule {
  return {
    field,
    check(value, row) {
      if (amountExceedsLimit(value, limit)) {
        return { rule: 'max-amount', limit };
      }
      const siblingValue = offendingSiblingAmount(row, field, limit);
      if (siblingValue !== null) {
        return { rule: 'max-amount', limit, siblingValue };
      }
      return null;
    },
  };
}

export function notLoadedRule(field: string): CellRule {
  return {
    field,
    check(value) {
      return value === null ? null : { rule: 'not-loaded' };
    },
  };
}

export function cannotClearRule(field: string): CellRule {
  return {
    field,
    check(value) {
      return value !== null && isSlashRun(value)
        ? { rule: 'cannot-clear' }
        : null;
    },
  };
}

export function requiredRule(field: string): CellRule {
  return {
    field,
    evenWhenUnedited: true,
    check(value) {
      if (value !== null && value.trim() !== '') return null;
      return { rule: 'required' };
    },
  };
}
